import { useEffect, useRef, useState } from "react";
import { getRespondentStats, getSegmentDistribution } from "../lib/api";
import { captureError } from "../lib/telemetry";
import type { Response, SegmentRow } from "../lib/types";

export type SegmentStats = {
  respondents: number;
  unlinked: number;
  segments: SegmentRow[];
  loading: boolean;
  error: boolean;
};

const EMPTY: SegmentStats = { respondents: 0, unlinked: 0, segments: [], loading: false, error: false };
const DEBOUNCE_MS = 500;

/**
 * k-anonymous respondent stats for the displayed question. Fetches on mount
 * and refetches (debounced, single-flight) whenever the responses array
 * changes identity — i.e. on every realtime insert/update and on the
 * reconciliation poll. On failure keeps the last good value so the default
 * views never depend on the RPCs being available. A fetch that resolves
 * after its effect was cleaned up (question switch, unmount) is ignored;
 * a queued re-run always targets the newest question.
 */
export function useSegmentStats(
  questionId: string | null,
  responses: Response[],
  enabled: boolean,
): SegmentStats {
  const [stats, setStats] = useState<SegmentStats>(EMPTY);
  const runningRef = useRef(false);
  const pendingRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const latestRunRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setStats(EMPTY);
  }, [questionId]);

  useEffect(() => {
    if (!questionId || !enabled) return;
    const qid = questionId;
    let cancelled = false;

    const run = async () => {
      if (runningRef.current) { pendingRef.current = true; return; }
      runningRef.current = true;
      setStats((s) => ({ ...s, loading: true }));
      try {
        const [base, segments] = await Promise.all([getRespondentStats(qid), getSegmentDistribution(qid)]);
        if (!cancelled) {
          setStats({ respondents: base.respondents, unlinked: base.unlinked_responses, segments, loading: false, error: false });
        }
      } catch (err) {
        captureError(err, { where: "useSegmentStats", questionId: qid });
        if (!cancelled) setStats((s) => ({ ...s, loading: false, error: true }));
      } finally {
        runningRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          // Re-run against the NEWEST effect's question, never this closure's.
          latestRunRef.current?.();
        }
      }
    };

    const runLatest = () => { void run(); };
    latestRunRef.current = runLatest;

    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => { timerRef.current = null; void run(); }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      if (timerRef.current != null) { window.clearTimeout(timerRef.current); timerRef.current = null; }
      if (latestRunRef.current === runLatest) latestRunRef.current = null;
    };
  }, [questionId, enabled, responses]);

  return stats;
}
